import { ACTION_KINDS, ROLES, type GameEvent, type GameView } from "@coup/engine";
import { z } from "zod";
import { avatarSchema } from "./avatar.js";

export const MIN_RESPONSE_MS = 3000;
export const MAX_RESPONSE_MS = 15000;
export const CODE_LETTERS = "BCDFGHJKMNPQRSTVWXYZ";

export const nameSchema = z
  .string()
  .trim()
  .min(1, "Enter a name")
  .max(16, "Names are 16 characters at most");
export const codeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(new RegExp(`^[${CODE_LETTERS}]{5}$`), "Codes are 5 letters");
export const responseMsSchema = z.number().int().min(MIN_RESPONSE_MS).max(MAX_RESPONSE_MS);

const role = z.enum(ROLES);

export const playerCommandSchema = z.discriminatedUnion("t", [
  z.object({ t: z.literal("declare"), kind: z.enum(ACTION_KINDS), target: z.string().optional() }),
  z.object({
    t: z.literal("respond"),
    windowId: z.number().int(),
    response: z.union([z.literal("allow"), z.literal("challenge"), z.object({ block: role })]),
  }),
  z.object({ t: z.literal("reveal") }),
  z.object({ t: z.literal("concede") }),
  z.object({ t: z.literal("loseCard"), index: z.number().int() }),
  z.object({ t: z.literal("keepCards"), indices: z.array(z.number().int()).max(4) }),
]);

const scenarioSchema = z.object({
  hands: z.record(z.string(), z.array(role).max(2)),
  coins: z.record(z.string(), z.number().int().min(0)).optional(),
  deck: z.array(role).optional(),
  turn: z.string().optional(),
  seed: z.number().int().optional(),
});

export const hostOpSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("start") }),
  z.object({ op: z.literal("end") }),
  z.object({ op: z.literal("again") }),
  z.object({ op: z.literal("kick"), playerId: z.string() }),
  z.object({ op: z.literal("responseMs"), ms: responseMsSchema }),
  z.object({ op: z.literal("shuffle") }),
  z.object({ op: z.literal("move"), playerId: z.string(), index: z.number().int().min(0) }),
]);

/** Dev-only messages; the server rejects them unless DEV_TOOLS=1. */
export const devOpSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("bots") }),
  /** Starts a match from a scenario; hand keys are seat indices ("0", "1", …). */
  z.object({ op: z.literal("scenario"), scenario: scenarioSchema }),
]);

export const clientMessageSchema = z.discriminatedUnion("t", [
  z.object({
    t: z.literal("create"),
    name: nameSchema,
    avatar: avatarSchema,
    responseMs: responseMsSchema,
  }),
  z.object({
    t: z.literal("join"),
    code: codeSchema,
    name: nameSchema.optional(),
    avatar: avatarSchema.optional(),
    token: z.string().optional(),
  }),
  z.object({
    t: z.literal("profile"),
    name: nameSchema.optional(),
    avatar: avatarSchema.optional(),
  }),
  z.object({ t: z.literal("host"), op: hostOpSchema }),
  z.object({ t: z.literal("cmd"), command: playerCommandSchema }),
  z.object({ t: z.literal("leave") }),
  /** A spectator takes a free seat between matches. */
  z.object({ t: z.literal("sit") }),
  z.object({ t: z.literal("dev"), op: devOpSchema }),
]);

export const seatViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar: avatarSchema,
  connected: z.boolean(),
  bot: z.boolean().optional(),
  /** Left or was kicked mid-match; kept until the match is cleared so the table can still name them. */
  left: z.boolean().optional(),
});

export const spectatorViewSchema = seatViewSchema.pick({
  id: true,
  name: true,
  avatar: true,
  connected: true,
});

export const lobbyPhaseSchema = z.enum(["lobby", "playing", "over"]);

export const serverMessageSchema = z.discriminatedUnion("t", [
  /** Sent to the joining socket only: its seat and the token to reclaim it. */
  z.object({ t: z.literal("joined"), code: z.string(), token: z.string(), you: z.string() }),
  z.object({
    t: z.literal("lobby"),
    code: z.string(),
    seats: z.array(seatViewSchema),
    spectators: z.array(spectatorViewSchema),
    hostId: z.string(),
    responseMs: z.number(),
    phase: lobbyPhaseSchema,
    devTools: z.boolean(),
  }),
  z.object({
    t: z.literal("game"),
    view: z.custom<GameView>(),
    events: z.array(z.custom<GameEvent>()),
    serverNow: z.number(),
  }),
  z.object({ t: z.literal("error"), code: z.string(), message: z.string() }),
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type HostOp = z.infer<typeof hostOpSchema>;
export type DevOp = z.infer<typeof devOpSchema>;
export type SeatView = z.infer<typeof seatViewSchema>;
export type SpectatorView = z.infer<typeof spectatorViewSchema>;
export type LobbyPhase = z.infer<typeof lobbyPhaseSchema>;
export type LobbyMessage = Extract<ServerMessage, { t: "lobby" }>;
export type GameMessage = Extract<ServerMessage, { t: "game" }>;
